import { ActionIcon, Badge, Group, ScrollArea, Select, SimpleGrid, Stack, Table, Text, TextInput, ThemeIcon } from "@mantine/core";
import { IconActivityHeartbeat, IconFilter, IconUser } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { DataTableShell, FilterBar, PageHeader, SortableTh, StatCard, useInfinitePage, useTableSort } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { formatDate } from "../../lib/format";
import { useQueryFilters } from "../../lib/useQueryFilters";

const AUDIT_ENTITY_OPTIONS = [
  { value: "user", label: "User" },
  { value: "candidate", label: "Candidate" },
  { value: "submission", label: "Submission" },
  { value: "job", label: "Job" },
  { value: "access_request", label: "Access Request" },
  { value: "company", label: "Company" },
  { value: "payout", label: "Payout" },
  { value: "system", label: "System" },
];

const AUDIT_LOG_FILTER_DEFAULTS = { action: "", entity: "" };
const AUDIT_LOG_DEBOUNCE_KEYS = ["action"] as const;
const AUDIT_LOG_FILTER_ALLOWED = { entity: AUDIT_ENTITY_OPTIONS.map((item) => item.value) };
const AUDIT_LOG_FILTER_OPTIONS = {
  debounceKeys: AUDIT_LOG_DEBOUNCE_KEYS,
  allowed: AUDIT_LOG_FILTER_ALLOWED,
};

export function AdminLogsPage() {
  const logFilters = useQueryFilters(AUDIT_LOG_FILTER_DEFAULTS, AUDIT_LOG_FILTER_OPTIONS);
  const action = logFilters.draft.action;
  const entityType = logFilters.draft.entity || null;
  const logsQuery = useInfinitePage(
    ["admin-audit-logs", logFilters.values.action, logFilters.values.entity],
    (page, pageSize) =>
      adminApi.listAuditLogs({
        page,
        page_size: pageSize,
        action: logFilters.values.action || undefined,
        entity_type: logFilters.values.entity || undefined,
      }),
  );
  const logs = logsQuery.items;
  const uniqueActors = new Set(logs.map((row) => row.actor_user_id).filter(Boolean));
  const uniqueActions = new Set(logs.map((row) => row.action));
  const stats = {
    total: logs.length,
    uniqueActors: uniqueActors.size,
    uniqueActions: uniqueActions.size,
  };
  const { sorted: sortedLogs, sortKey, direction, toggle } = useTableSort(logs, {
    timestamp: (r) => r.created_at,
    action: (r) => r.action,
    actor: (r) => r.actor_name,
    entity: (r) => r.entity_type,
    details: (r) => (Object.keys(r.detail).length > 0 ? JSON.stringify(r.detail) : null),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.logs}
        title={NAV_COPY.admin.logs}
        subtitle="Platform activity and system events, newest first."
      />

      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
        <StatCard label="Showing events" value={stats.total} icon={IconActivityHeartbeat} />
        <StatCard label="Unique actors" value={stats.uniqueActors} icon={IconUser} />
        <StatCard label="Unique actions" value={stats.uniqueActions} icon={IconFilter} />
      </SimpleGrid>

      <FilterBar>
        <TextInput
          label="Action filter"
          placeholder="user.login, candidate.updated..."
          value={action}
          onChange={(e) => logFilters.set({ action: e.currentTarget.value })}
          w="var(--axio-unit-360)"
        />
        <Select
          label="Entity type"
          placeholder="All entities"
          value={entityType}
          onChange={(value) => logFilters.set({ entity: value ?? "" })}
          clearable
          data={AUDIT_ENTITY_OPTIONS}
          w="var(--axio-unit-220)"
        />
      </FilterBar>

      <DataTableShell
        loading={logsQuery.isLoading}
        error={
          logsQuery.isError
            ? logsQuery.error instanceof Error
              ? logsQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void logsQuery.refetch()}
        hasMore={logsQuery.hasMore}
        loadingMore={logsQuery.loadingMore}
        onLoadMore={logsQuery.loadMore}
        isEmpty={logs.length === 0}
        empty={{
          icon: NAV_ICONS.logs,
          title: "No audit logs yet",
          description: "Platform activity will be recorded here as events occur.",
        }}
      >
        <ScrollArea>
          <Table verticalSpacing="md" highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <SortableTh columnKey="timestamp" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Timestamp
                </SortableTh>
                <SortableTh columnKey="action" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Action
                </SortableTh>
                <SortableTh columnKey="actor" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Actor
                </SortableTh>
                <SortableTh columnKey="entity" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Entity
                </SortableTh>
                <SortableTh columnKey="details" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Details
                </SortableTh>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedLogs.map((log) => (
                <Table.Tr key={log.id}>
                  <Table.Td>
                    <Text size="sm">{formatDate(log.created_at)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" color="gray">
                      {log.action}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="var(--axio-unit-6)">
                      <ThemeIcon variant="light" size="var(--axio-unit-20)" color="gray">
                        <IconUser size="var(--axio-unit-12)" />
                      </ThemeIcon>
                      <Text size="sm">{log.actor_name ?? "System"}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="var(--axio-unit-8)">
                      <Badge variant="outline" color="gray">
                        {log.entity_type}
                      </Badge>
                      {log.entity_type === "user" && log.entity_id ? (
                        <ActionIcon
                          component={Link}
                          to={`/admin/people/${log.entity_id}`}
                          variant="default"
                          size="sm"
                          aria-label="Open user profile"
                          title="Open user profile"
                        >
                          <IconUser size="var(--axio-unit-14)" />
                        </ActionIcon>
                      ) : null}
                      {log.entity_type === "candidate" && log.entity_id ? (
                        <ActionIcon
                          component={Link}
                          to={`/admin/candidates/${log.entity_id}`}
                          variant="default"
                          size="sm"
                          aria-label="Open candidate profile"
                          title="Open candidate profile"
                        >
                          <IconUser size="var(--axio-unit-14)" />
                        </ActionIcon>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {Object.keys(log.detail).length > 0 ? (
                      <Stack gap="var(--axio-unit-2)">
                        {Object.entries(log.detail).map(([key, value]) => (
                          <Text key={key} size="xs" c="dimmed">
                            {key}: {String(value)}
                          </Text>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </DataTableShell>
    </Stack>
  );
}
